import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  TextField,
  MenuItem,
  Chip,
  Divider,
  List,
  ListItemButton,
  Avatar,
  Button,
  Tooltip,
  TextField as MuiTextField,
  Snackbar,
  Alert,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import CircleIcon from '@mui/icons-material/Circle';
import FolderIcon from '@mui/icons-material/Folder';
import SendIcon from '@mui/icons-material/Send';
import PanToolAltIcon from '@mui/icons-material/PanToolAlt';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {
  LivePlayer,
  NativeLivePlayer,
  extractInspectors,
  EventType,
  type InspectorData,
  type ReplayEvent,
} from '@replayops/replay-core';
import { useNavigate } from 'react-router-dom';
import { CreateIssueDialog } from '../components/CreateIssueDialog.js';
import { useMe } from '../api/hooks.js';
import { useActiveProject } from '../lib/activeProject.js';
import { ensureAccessToken } from '../api/client.js';
import { LiveDevTools } from '../components/LiveDevTools.js';
import { useT } from '../i18n/useT.js';

interface Presence {
  sessionId: string;
  /** Stamped by the operator gateway; present when watching several projects. */
  projectId?: string;
  url: string;
  identity: { anonymousId: string; userId?: string; email?: string };
  meta: { browser?: string; os?: string; deviceType?: string; platform?: 'web' | 'ios' | 'android' };
  startedAt: number;
  lastSeen: number;
}
interface ChatMsg {
  from: 'visitor' | 'agent';
  text: string;
}

function who(p: Presence): string {
  return p.identity.email || p.identity.userId || `anon ${p.identity.anonymousId.slice(0, 8)}`;
}
function shortPath(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + u.search + u.hash || '/';
  } catch {
    return url;
  }
}

/** Sentinel understood by the operator gateway: watch every project in the org. */
const ALL_PROJECTS = '*';

export function LivePage() {
  const t = useT().live;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const navigate = useNavigate();
  // Only members granted remote-control in user management may drive the visitor.
  const canControl = !!me?.organizations[0]?.remoteControl;
  // Visitors are watched across every project by default — picking one project
  // up front is how a visitor on another project goes unnoticed.
  const { selected: chosenProject, setProjectId: storeProject, projects } = useActiveProject(orgId);
  // Live's "all" is '*' on the wire; the shared choice spells it ''.
  const activeProject = chosenProject || ALL_PROJECTS;
  const setProjectId = (id: string) => storeProject(id === ALL_PROJECTS ? '' : id);

  const [presence, setPresence] = useState<Map<string, Presence>>(new Map());
  const [selected, setSelected] = useState<string | null>(null);
  /** The session the operator was watching when it ended — the file-a-bug moment. */
  const [ended, setEnded] = useState<{ sessionId: string; projectId: string; url: string } | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const [chats, setChats] = useState<Record<string, ChatMsg[]>>({});
  const [draft, setDraft] = useState('');
  const [control, setControl] = useState<'idle' | 'requested' | 'active'>('idle');

  const wsRef = useRef<WebSocket | null>(null);
  const selectedRef = useRef<string | null>(null);
  const [liveDt, setLiveDt] = useState<InspectorData | null>(null);
  const dtBufRef = useRef<ReplayEvent[]>([]); // plugin events since co-watch began
  // A native visitor streams frames, a web one streams DOM mutations; both
  // players expose the same two methods, so nothing below has to branch.
  const playerRef = useRef<LivePlayer | NativeLivePlayer | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null); // rrweb root (natural size); scaled + centered
  const stageWrapRef = useRef<HTMLDivElement | null>(null); // available area
  const dimsRef = useRef({ w: 0, h: 0 });
  const viewRef = useRef({ scale: 1, offsetX: 0, offsetY: 0 }); // current stage transform, for coord mapping
  selectedRef.current = selected;

  // Fit the visitor's screen into the available box (contain) and center it.
  // rrweb sizes the iframe inside stageRef to the visitor's viewport; we scale
  // that box down with a transform and offset it to the middle of the pane.
  const applyScale = useCallback(() => {
    const wrap = stageWrapRef.current;
    const stage = stageRef.current;
    const { w, h } = dimsRef.current;
    if (!wrap || !stage || !w || !h) return;
    const scale = Math.min(wrap.clientWidth / w, wrap.clientHeight / h, 1);
    const offsetX = Math.max(0, (wrap.clientWidth - w * scale) / 2);
    const offsetY = Math.max(0, (wrap.clientHeight - h * scale) / 2);
    stage.style.transform = `scale(${scale})`;
    stage.style.left = `${offsetX}px`;
    stage.style.top = `${offsetY}px`;
    viewRef.current = { scale, offsetX, offsetY }; // remember for control coord mapping
  }, []);

  // ── Operator WebSocket lifecycle ──
  useEffect(() => {
    if (!orgId || !activeProject) return;
    let stop = false;
    let ws: WebSocket;

    const connect = async () => {
      // The live WS has no 401 interceptor, so refresh a stale access token
      // BEFORE connecting — otherwise an expired token silently fails the
      // handshake (the operator gateway rejects it) and the reconnect loop
      // keeps re-using the same expired token.
      const token = await ensureAccessToken();
      if (stop || !token) return;
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${proto}://${location.host}/ws/live?token=${encodeURIComponent(token)}&orgId=${orgId}&projectId=${activeProject}`);
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (!stop) setTimeout(connect, 3000);
      };
      ws.onmessage = (ev) => {
        let m: Record<string, unknown>;
        try {
          m = JSON.parse(String(ev.data));
        } catch {
          return;
        }
        handle(m);
      };
    };

    const handle = (m: Record<string, unknown>) => {
      switch (m.t) {
        case 'presence':
          setPresence(new Map((m.list as Presence[]).map((p) => [p.sessionId, p])));
          break;
        case 'join':
          setPresence((prev) => new Map(prev).set((m.presence as Presence).sessionId, m.presence as Presence));
          break;
        case 'nav':
          setPresence((prev) => {
            const next = new Map(prev);
            const p = next.get(m.sessionId as string);
            if (p) next.set(p.sessionId, { ...p, url: m.url as string });
            return next;
          });
          break;
        case 'leave':
          setPresence((prev) => {
            const next = new Map(prev);
            const gone = prev.get(m.sessionId as string);
            next.delete(m.sessionId as string);
            // The operator was watching this one: the session just became a
            // finished recording, which is exactly when "that was a bug" is
            // decided — offer to file it rather than silently going blank.
            if (selectedRef.current === m.sessionId && gone) {
              setEnded({ sessionId: gone.sessionId, projectId: gone.projectId ?? '', url: gone.url ?? '' });
            }
            return next;
          });
          if (selectedRef.current === m.sessionId) setSelected(null);
          break;
        case 'evt':
          if (m.sessionId === selectedRef.current) {
            const ev = m.e as ReplayEvent;
            playerRef.current?.addEvent(ev as never);
            // Buffer plugin events (console/network/error/perf) for the live DevTools.
            if (ev.type === EventType.Plugin) {
              const buf = dtBufRef.current;
              buf.push(ev);
              if (buf.length > 3000) buf.shift();
            }
          }
          break;
        case 'chat':
          setChats((prev) => ({
            ...prev,
            [m.sessionId as string]: [
              ...(prev[m.sessionId as string] ?? []),
              { from: m.from as 'visitor' | 'agent', text: m.text as string },
            ],
          }));
          break;
        case 'control':
          // The visitor granted (on) or revoked/declined (off) control.
          if (m.sessionId === selectedRef.current) setControl(m.on ? 'active' : 'idle');
          break;
        case 'control-denied':
          // Server rejected the request — this operator lacks the capability.
          if (m.sessionId === selectedRef.current) setControl('idle');
          break;
      }
    };

    connect();
    return () => {
      stop = true;
      wsRef.current?.close();
    };
  }, [orgId, activeProject]);

  // ── Co-watch lifecycle for the selected visitor ──
  useEffect(() => {
    const ws = wsRef.current;
    if (!selected || !ws || ws.readyState !== WebSocket.OPEN || !stageRef.current) return;

    // Pick the player from the visitor's platform: a phone has no DOM to
    // reconstruct, so its stream is frames rather than mutations.
    const platform = presence.get(selected)?.meta?.platform;
    const isNative = platform === 'ios' || platform === 'android';
    const player = isNative
      ? new NativeLivePlayer(stageRef.current)
      : new LivePlayer(stageRef.current);
    playerRef.current = player;
    player.onDimensions((w, h) => {
      dimsRef.current = { w, h };
      applyScale();
    });
    ws.send(JSON.stringify({ t: 'watch', sessionId: selected }));
    setControl('idle');

    // Live DevTools: reset the buffer for this visitor and recompute the
    // inspector streams a few times a second as events stream in.
    dtBufRef.current = [];
    setLiveDt(null);
    const dtTimer = setInterval(() => {
      setLiveDt(dtBufRef.current.length ? extractInspectors([...dtBufRef.current]) : null);
    }, 800);

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ t: 'control-end', sessionId: selected }));
        ws.send(JSON.stringify({ t: 'unwatch', sessionId: selected }));
      }
      clearInterval(dtTimer);
      dtBufRef.current = [];
      setLiveDt(null);
      player.destroy();
      playerRef.current = null;
      dimsRef.current = { w: 0, h: 0 };
      setControl('idle');
    };
  }, [selected, applyScale]);

  // Re-fit when the pane or window resizes.
  useEffect(() => {
    const wrap = stageWrapRef.current;
    if (!wrap) return;
    window.addEventListener('resize', applyScale);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => applyScale());
      ro.observe(wrap);
    }
    return () => {
      window.removeEventListener('resize', applyScale);
      ro?.disconnect();
    };
  }, [selected, applyScale]);

  const send = () => {
    const text = draft.trim();
    const ws = wsRef.current;
    if (!text || !selected || !ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ t: 'chat', sessionId: selected, text }));
    setDraft('');
  };

  // ── Remote control ──
  const sendCtl = (msg: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (selected && ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ ...msg, sessionId: selected }));
  };
  const requestControl = () => {
    if (control !== 'idle') return;
    setControl('requested');
    sendCtl({ t: 'control-request' });
  };
  const stopControl = () => {
    sendCtl({ t: 'control-end' });
    setControl('idle');
  };
  const sendCmd = (cmd: Record<string, unknown>) => sendCtl({ t: 'control-cmd', cmd });

  // Map an operator pointer event to normalized (0..1) visitor-viewport coords.
  const toNorm = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const wrap = stageWrapRef.current;
    const { w, h } = dimsRef.current;
    const { scale, offsetX, offsetY } = viewRef.current;
    if (!wrap || !w || !h) return null;
    const r = wrap.getBoundingClientRect();
    const vx = (clientX - r.left - offsetX) / scale;
    const vy = (clientY - r.top - offsetY) / scale;
    return { x: vx / w, y: vy / h };
  };
  const inBounds = (p: { x: number; y: number }) => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1;

  const lastCursor = useRef(0);
  const onOverlayMove = (e: React.MouseEvent) => {
    const now = Date.now();
    if (now - lastCursor.current < 45) return; // throttle cursor updates
    lastCursor.current = now;
    const p = toNorm(e.clientX, e.clientY);
    if (p && inBounds(p)) sendCmd({ c: 'cursor', x: p.x, y: p.y });
  };
  const onOverlayClick = (e: React.MouseEvent) => {
    const p = toNorm(e.clientX, e.clientY);
    if (p && inBounds(p)) sendCmd({ c: 'click', x: p.x, y: p.y });
    (e.currentTarget as HTMLElement).focus(); // keep key capture on the overlay
  };
  const onOverlayWheel = (e: React.WheelEvent) => sendCmd({ c: 'scroll', x: e.deltaX, y: e.deltaY });
  const onOverlayKey = (e: React.KeyboardEvent) => {
    const printable = e.key.length === 1;
    if (printable || e.key === 'Enter' || e.key === 'Backspace') {
      e.preventDefault();
      sendCmd({ c: 'key', key: e.key });
    }
  };

  const visitors = [...presence.values()].sort((a, b) => b.lastSeen - a.lastSeen);
  const projectName = (id: string) => projects.find((p) => p.id === id)?.name ?? t.unknownProject;
  const onlinePerProject = visitors.reduce((m, p) => {
    if (p.projectId) m.set(p.projectId, (m.get(p.projectId) ?? 0) + 1);
    return m;
  }, new Map<string, number>());
  const active = selected ? presence.get(selected) : null;
  const thread = selected ? chats[selected] ?? [] : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { md: 'calc(100vh - 128px)' } }}>
      {/* The visitor left while being watched: the session is now a finished
          recording, and this is the moment "that was a bug" gets decided. */}
      <Snackbar
        open={!!ended && !issueOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={(_, reason) => reason !== 'clickaway' && setEnded(null)}
      >
        <Alert
          severity="info"
          onClose={() => setEnded(null)}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button color="inherit" size="small" variant="outlined" startIcon={<BugReportIcon />} onClick={() => setIssueOpen(true)}>
                {t.openAsBug}
              </Button>
              {ended?.projectId && (
                <Button color="inherit" size="small" onClick={() => navigate(`/projects/${ended.projectId}/sessions/${ended.sessionId}`)}>
                  {t.watchReplay}
                </Button>
              )}
            </Stack>
          }
        >
          {t.ended(ended?.sessionId.slice(0, 8) ?? '', ended?.url ?? '')}
        </Alert>
      </Snackbar>

      {ended && orgId && ended.projectId && (
        <CreateIssueDialog
          open={issueOpen}
          onClose={() => {
            setIssueOpen(false);
            setEnded(null);
          }}
          orgId={orgId}
          projectId={ended.projectId}
          sessionId={ended.sessionId}
          defaultTitle={t.issueTitle(ended.url || ended.sessionId.slice(0, 8))}
          description={t.issueBody(ended.url || '—')}
        />
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h5">{t.title}</Typography>
          <Chip
            size="small"
            icon={<CircleIcon sx={{ fontSize: 10 }} />}
            color={connected ? 'success' : 'default'}
            label={connected ? t.online(visitors.length) : t.connecting}
          />
        </Stack>
        {/* Which project you are watching decides who shows up at all, so it is
            a first-class control here rather than a small filter in the corner. */}
        <TextField
          select
          label={t.watching}
          value={activeProject}
          onChange={(e) => setProjectId(e.target.value)}
          sx={{ minWidth: 260, '& .MuiInputBase-input': { fontWeight: 700 } }}
          InputProps={{ startAdornment: <FolderIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
        >
          <MenuItem value={ALL_PROJECTS} sx={{ fontWeight: 700 }}>
            {t.allProjects}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({projects.length})
            </Typography>
          </MenuItem>
          <Divider />
          {projects.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {onlinePerProject.get(p.id) ? t.onlineSuffix(onlinePerProject.get(p.id)!) : ''}
              </Typography>
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Active visitors */}
        <Paper sx={{ width: { md: 272 }, flexShrink: 0, overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ p: 2, pb: 1 }}>
            {t.activeVisitors}
          </Typography>
          {visitors.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
              {t.noVisitors}
            </Typography>
          )}
          <List dense>
            {visitors.map((p) => (
              <ListItemButton key={p.sessionId} selected={p.sessionId === selected} onClick={() => setSelected(p.sessionId)} sx={{ alignItems: 'flex-start' }}>
                <Avatar sx={{ width: 30, height: 30, mr: 1.5, mt: 0.3, bgcolor: 'primary.main', fontSize: 13 }}>{who(p).charAt(0).toUpperCase()}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {who(p)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {shortPath(p.url)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.meta.browser} · {p.meta.deviceType}
                  </Typography>
                  {activeProject === ALL_PROJECTS && p.projectId && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={projectName(p.projectId)}
                      sx={{ height: 18, fontSize: 10, mt: 0.4, maxWidth: '100%' }}
                    />
                  )}
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* Co-watch + chat */}
        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 520, md: 0 }, overflow: 'hidden' }}>
          {!active && (
            <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">{t.selectVisitor}</Typography>
            </Box>
          )}
          {active && (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {who(active)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {active.url}
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {control === 'idle' && canControl && (
                    <Button size="small" variant="outlined" startIcon={<PanToolAltIcon />} onClick={requestControl}>
                      {t.requestControl}
                    </Button>
                  )}
                  {control === 'idle' && !canControl && (
                    <Tooltip title={t.controlDisabled}>
                      <span>
                        <Button size="small" variant="outlined" startIcon={<PanToolAltIcon />} disabled>
                          {t.requestControl}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {control === 'requested' && (
                    <Button size="small" variant="outlined" color="warning" onClick={stopControl}>
                      {t.waitingConsent}
                    </Button>
                  )}
                  {control === 'active' && (
                    <Button size="small" variant="contained" color="error" startIcon={<StopCircleIcon />} onClick={stopControl}>
                      {t.stopControl}
                    </Button>
                  )}
                  <Chip
                    size="small"
                    color={control === 'active' ? 'error' : 'success'}
                    icon={<CircleIcon sx={{ fontSize: 9 }} />}
                    label={control === 'active' ? t.controlling : t.live}
                  />
                </Stack>
              </Stack>

              {/* Screen (fills) + chat column beside it */}
              <Stack direction={{ xs: 'column', md: 'row' }} sx={{ flex: 1, minHeight: 0 }}>
                <Box
                  ref={stageWrapRef}
                  sx={{ flex: 1, minHeight: { xs: 280, md: 0 }, position: 'relative', overflow: 'hidden', bgcolor: '#0b0d14' }}
                >
                  <div ref={stageRef} style={{ position: 'absolute', top: 0, left: 0, transformOrigin: 'top left', background: '#fff' }} />
                  {/* Transparent capture layer. Forwards operator input as control commands. */}
                  {control === 'active' && (
                    <Box
                      tabIndex={0}
                      onMouseMove={onOverlayMove}
                      onClick={onOverlayClick}
                      onWheel={onOverlayWheel}
                      onKeyDown={onOverlayKey}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        cursor: 'crosshair',
                        outline: '2px solid',
                        outlineColor: 'error.main',
                        outlineOffset: '-2px',
                        overscrollBehavior: 'contain',
                        zIndex: 5,
                      }}
                    />
                  )}
                </Box>

                <Stack
                  sx={{
                    width: { xs: '100%', md: 300 },
                    flexShrink: 0,
                    borderColor: 'divider',
                    borderTop: { xs: 1, md: 0 },
                    borderLeft: { xs: 0, md: 1 },
                    minHeight: 0,
                  }}
                >
                  <Box sx={{ flex: 1, p: 1.5, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75, minHeight: { xs: 140, md: 0 } }}>
                    {thread.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {t.noMessages}
                      </Typography>
                    )}
                    {thread.map((m, i) => (
                      <Box
                        key={i}
                        sx={{
                          alignSelf: m.from === 'agent' ? 'flex-end' : 'flex-start',
                          bgcolor: m.from === 'agent' ? 'primary.main' : 'action.hover',
                          color: m.from === 'agent' ? '#fff' : 'text.primary',
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 2,
                          maxWidth: '85%',
                          fontSize: 14,
                          wordBreak: 'break-word',
                        }}
                      >
                        {m.text}
                      </Box>
                    ))}
                  </Box>
                  <Stack direction="row" spacing={1} sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
                    <MuiTextField size="small" fullWidth placeholder={t.messagePlaceholder} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} />
                    <Button variant="contained" onClick={send} disabled={!draft.trim()} sx={{ minWidth: 44 }}>
                      <SendIcon fontSize="small" />
                    </Button>
                  </Stack>
                </Stack>
              </Stack>

              {/* Live DevTools. Network/console/errors/perf since watching began. */}
              <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, pt: 0.75 }}>
                  {t.devTools}
                </Typography>
                <Box sx={{ p: 1, pt: 0.5 }}>
                  <LiveDevTools data={liveDt} />
                </Box>
              </Box>
            </>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
